import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  RefreshCw,
  PlusCircle,
  AlertTriangle,
  FileText,
  Search,
  X,
  Calendar,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import AppliedFilters from '@/components/AppliedFilters';
import ExportExcelButton from '@/components/ExportExcelButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import BoardPageTransition from '@/components/operations/BoardPageTransition';
import TraceabilitySection from '@/pages/traceability/components/TraceabilitySection';
import AirRequestDetailDialog from '../components/AirRequestDetailDialog';
import ProviderInvitationModal from '../components/ProviderInvitationModal';
import CargarAirCotizacionDialog from '../cotizaciones/components/CargarAirCotizacionDialog.jsx';
import ComparadorCotizacionesAereoDialog from '../cotizaciones/components/ComparadorCotizacionesAereoDialog.jsx';
import {
  fetchRequests,
  deleteRequest,
  setEstado,
  updateRequest,
} from '../Services/requestsService';
import { shortId, formatCodigo, formatCliente, formatRuta, formatDate, kg } from '../utils/airFormatters';
import { ITEMS_PER_PAGE, VENCIDA_DIAS, SUBESTADO_BADGE, daysSince } from '../utils/airBoardConstants';

/**
 * Tablero base de los 4 boards del módulo aéreo.
 *
 * Gemelo de `LclBoardPage`: la parte común vive una sola vez y cada
 * tablero (`QuotationsBoardPage`, `InProgressBoardPage`, …) sólo aporta su
 * configuración. Es código 100% del aéreo: no se comparte nada con el LCL,
 * para que cada operación pueda tener sus tareas sin tocar la otra.
 *
 * Props de configuración:
 *  - estado:            fase principal que filtra el tablero
 *  - titulo, subtitulo: encabezado
 *  - icon, iconClass:   icono del encabezado
 *  - subEstadoOptions:  [{value,label}] de las métricas y del filtro
 *  - subEstadoDefault:  sub-estado que se asume cuando la fila no tiene
 *  - computeSubEstado:  (row) => string — opcional, para sub-estados
 *                       derivados (ej. "Vencida" por antigüedad)
 *  - mostrarAntiguedad: columna de días desde la última actualización
 *  - exportFileName:    prefijo del archivo Excel
 *  - permiteInvitar:    "Solicitar cotización" a proveedores
 *  - permiteCargarCotizacion: "Cargar cotización de proveedor"
 *  - accionesEstado:    acciones que mueven la solicitud de tablero:
 *      [{ key, icon, tooltip, className,
 *         confirmar: (codigo) => string | null,   // texto del confirm
 *         cambio: { estado } | { subEstado },     // qué se guarda
 *         exito, error }]                         // textos del aviso
 */
export default function AirBoardPage({
  estado,
  titulo,
  subtitulo,
  icon: Icon = FileText,
  iconClass = 'bg-sky-100 text-sky-700',
  subEstadoOptions = [],
  subEstadoDefault = '',
  computeSubEstado,
  mostrarAntiguedad = false,
  exportFileName = 'aereo',
  permiteInvitar = false,
  permiteCargarCotizacion = false,
  accionesEstado = [],
}) {
  const toast = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterSubEstado, setFilterSubEstado] = useState('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Acciones
  const [detailRequest, setDetailRequest] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [traceabilityRequest, setTraceabilityRequest] = useState(null);
  const [invitationRequest, setInvitationRequest] = useState(null);
  const [cotizacionRequest, setCotizacionRequest] = useState(null);
  const [comparadorRequest, setComparadorRequest] = useState(null);

  const resolveSubEstado = useCallback(
    (r) => {
      if (computeSubEstado) return computeSubEstado(r);
      return r?.subEstado || subEstadoDefault;
    },
    [computeSubEstado, subEstadoDefault],
  );

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);

  const load = useCallback(
    async ({ silencioso = false } = {}) => {
      if (!silencioso) setLoading(true);
      try {
        const query = { estado };
        if (filterFechaDesde || filterFechaHasta) {
          query.createdAt = {};
          if (filterFechaDesde) query.createdAt.$gte = new Date(filterFechaDesde);
          if (filterFechaHasta) query.createdAt.$lte = new Date(filterFechaHasta);
        }
        const r = await fetchRequests({
          limit: 200, // se trae todo y se filtra/pagina en el cliente
          offset: 1,
          query,
          sort: { updatedAt: -1 },
        });
        setRows(r?.data || []);
      } catch (e) {
        if (!silencioso) toast.error(`Error al cargar ${titulo.toLowerCase()}`, e?.message);
      } finally {
        if (!silencioso) setLoading(false);
      }
    },
    [estado, filterFechaDesde, filterFechaHasta, titulo, toast],
  );

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.toLowerCase();
    return rows
      .map((r) => ({ ...r, subEstado: resolveSubEstado(r) }))
      .filter((r) => {
        if (filterSubEstado && r.subEstado !== filterSubEstado) return false;
        if (!q) return true;
        return [
          r.codigo,
          r.clienteSnapshot?.nombre,
          r.clienteSnapshot?.nit,
          r.aeropuertoCargaSnapshot?.iata,
          r.aeropuertoCargaSnapshot?.ciudad,
          r.aeropuertoDescargaSnapshot?.iata,
          r.aeropuertoDescargaSnapshot?.ciudad,
        ]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });
  }, [rows, debouncedSearch, filterSubEstado, resolveSubEstado]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterSubEstado, filterFechaDesde, filterFechaHasta]);

  const pageItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filtered.slice(start, start + ITEMS_PER_PAGE);
  }, [filtered, currentPage]);

  const filtrosActivos = Boolean(search || filterSubEstado || filterFechaDesde || filterFechaHasta);

  const limpiarFiltros = () => {
    setSearch('');
    setFilterSubEstado('');
    setFilterFechaDesde('');
    setFilterFechaHasta('');
  };

  // ---- Acciones de estado (finalizar, cancelar, reabrir, archivar…) ----
  const ejecutarAccionEstado = useCallback(
    async (accion, row) => {
      const codigo = formatCodigo(row);
      const pregunta = accion.confirmar?.(codigo);
      if (pregunta && !window.confirm(pregunta)) return;
      const r = accion.cambio.estado
        ? await setEstado(row._id, accion.cambio.estado)
        : await updateRequest(row._id, accion.cambio);
      if (r?.success) {
        toast.success(accion.exito, codigo);
        load();
      } else {
        toast.error(accion.error, r?.message);
      }
    },
    [load, toast],
  );

  const handleTableAction = useCallback(
    (action, row) => {
      const accionEstado = accionesEstado.find((a) => a.key === action);
      if (accionEstado) {
        ejecutarAccionEstado(accionEstado, row);
        return;
      }
      switch (action) {
        case 'view':
          setDetailRequest(row);
          break;
        case 'traceability':
          setTraceabilityRequest(row);
          break;
        case 'invite':
          setInvitationRequest(row);
          break;
        case 'uploadQuote':
          setCotizacionRequest(row);
          break;
        case 'compareQuotes':
          setComparadorRequest(row);
          break;
        case 'delete':
          setDeleteTarget(row);
          break;
        default:
          break;
      }
    },
    [accionesEstado, ejecutarAccionEstado],
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const label = formatCodigo(deleteTarget);
    const r = await deleteRequest(deleteTarget._id);
    if (r?.success) {
      toast.success('Solicitud eliminada', label);
      load();
    } else {
      toast.error('No se pudo eliminar', r?.message);
    }
    setDeleteTarget(null);
  };

  const metrics = useMemo(() => {
    const out = {};
    for (const opt of subEstadoOptions) out[opt.value] = 0;
    for (const r of rows) {
      const se = resolveSubEstado(r);
      if (se in out) out[se] += 1;
    }
    return out;
  }, [rows, subEstadoOptions, resolveSubEstado]);

  // ---- Columnas ----
  const tableColumns = useMemo(() => {
    const cols = [
      {
        id: 'codigo',
        label: 'Código',
        sortable: true,
        render: (row) => <span className="font-mono text-xs font-semibold">{formatCodigo(row)}</span>,
      },
      {
        id: 'cliente',
        label: 'Cliente',
        sortable: true,
        render: (row) => (
          <div className="min-w-0">
            <div className="text-sm font-medium truncate" title={formatCliente(row)}>
              {formatCliente(row)}
            </div>
            {row.clienteSnapshot?.nit && (
              <div className="text-[10px] text-muted-foreground font-mono">NIT {row.clienteSnapshot.nit}</div>
            )}
          </div>
        ),
      },
      {
        id: 'ruta',
        label: 'Ruta',
        render: (row) => <span className="text-xs text-muted-foreground">{formatRuta(row)}</span>,
      },
      {
        id: 'carga',
        label: 'Carga',
        render: (row) => (
          <div className="text-xs text-muted-foreground tabular-nums">
            <div>
              {row.numPiezas ?? 0} piezas · {kg(row.pesoTotal)} kg
            </div>
            <div>
              vol. {kg(row.pesoVolumetrico)} kg ·{' '}
              <span className="font-semibold text-foreground" title="Peso cobrable">
                {kg(row.pesoCobrable ?? Math.max(row.pesoTotal || 0, row.pesoVolumetrico || 0))} kg cobr.
              </span>
            </div>
          </div>
        ),
      },
      {
        id: 'subEstado',
        label: 'Sub-estado',
        sortable: true,
        render: (row) => {
          const se = resolveSubEstado(row);
          if (!se) return <span className="text-xs text-muted-foreground">—</span>;
          return (
            <Badge variant="outline" className={cn('font-normal text-[10px] py-0 px-1.5', SUBESTADO_BADGE[se])}>
              {se}
            </Badge>
          );
        },
      },
    ];

    if (mostrarAntiguedad) {
      cols.push({
        id: 'antiguedad',
        label: 'Antigüedad',
        sortable: true,
        render: (row) => {
          const d = daysSince(row.updatedAt || row.createdAt);
          return (
            <span
              className={cn(
                'text-xs tabular-nums font-mono',
                d > VENCIDA_DIAS ? 'text-rose-600 font-semibold' : 'text-muted-foreground',
              )}
            >
              {d}d
            </span>
          );
        },
      });
    }

    cols.push(
      {
        id: 'updatedAt',
        label: 'Actualizado',
        sortable: true,
        render: (row) => (
          <span className="text-xs text-muted-foreground">{formatDate(row.updatedAt || row.createdAt)}</span>
        ),
      },
      {
        id: 'actions',
        label: 'Acciones',
        actions: [
          { key: 'view', icon: 'Eye', tooltip: 'Ver detalle' },
          { key: 'traceability', icon: 'Hash', tooltip: 'Trazabilidad' },
          ...(permiteInvitar ? [{ key: 'invite', icon: 'Send', tooltip: 'Solicitar cotización' }] : []),
          ...(permiteCargarCotizacion
            ? [
                { key: 'uploadQuote', icon: 'Upload', tooltip: 'Cargar cotización de proveedor' },
                { key: 'compareQuotes', icon: 'Scale', tooltip: 'Comparar cotizaciones' },
              ]
            : []),
          ...accionesEstado.map(({ key, icon, tooltip, className }) => ({ key, icon, tooltip, className })),
          // Una operación en curso no se elimina (el backend también lo
          // impide): si no sigue, se cancela.
          ...(estado === 'Enviada'
            ? []
            : [
                {
                  key: 'delete',
                  icon: 'Trash',
                  tooltip: 'Eliminar solicitud',
                  className: 'hover:bg-destructive hover:text-destructive-foreground',
                },
              ]),
        ],
      },
    );
    return cols;
  }, [mostrarAntiguedad, resolveSubEstado, permiteInvitar, permiteCargarCotizacion, accionesEstado, estado]);

  const excelColumns = useMemo(
    () => [
      { key: 'codigo', header: 'Código' },
      { key: 'clienteSnapshot.nombre', header: 'Cliente' },
      { key: 'clienteSnapshot.nit', header: 'NIT' },
      { key: 'aeropuertoCargaSnapshot.iata', header: 'Origen' },
      { key: 'aeropuertoDescargaSnapshot.iata', header: 'Destino' },
      { key: 'numPiezas', header: 'Piezas' },
      { key: 'pesoTotal', header: 'Peso bruto (kg)' },
      { key: 'pesoVolumetrico', header: 'Peso volumétrico (kg)' },
      { key: 'pesoCobrable', header: 'Peso cobrable (kg)' },
      { key: 'incotermSnapshot.codigo', header: 'Incoterm' },
      { key: 'estado', header: 'Estado' },
      { key: 'subEstado', header: 'Sub-estado' },
      { key: 'createdAt', header: 'Creada' },
      { key: 'updatedAt', header: 'Actualizada' },
    ],
    [],
  );

  const exportService = useCallback(
    async ({ query = {}, sort } = {}) =>
      fetchRequests({ limit: 10000, offset: 1, query: { ...query, estado }, sort: sort || { createdAt: -1 } }),
    [estado],
  );

  return (
    <BoardPageTransition>
      <div className="space-y-4">
        {/* Encabezado */}
        <Card>
          <CardContent className="p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" onClick={() => navigate('/requests')} aria-label="Volver al tablero aéreo">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className={cn('p-2 rounded-lg', iconClass)}>
                  <Icon className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl font-bold text-foreground">{titulo}</h1>
                  <p className="text-xs text-muted-foreground">{subtitulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ExportExcelButton
                  service={exportService}
                  columns={excelColumns}
                  fileName={`${exportFileName}_${new Date().toISOString().slice(0, 10)}`}
                  filters={{ estado }}
                />
                <Button variant="outline" size="sm" onClick={() => load()} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
                <Button size="sm" variant="outline" onClick={() => navigate('/requests')}>
                  <PlusCircle className="h-4 w-4" />
                  Nueva
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Métricas de sub-estado */}
        {subEstadoOptions.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {subEstadoOptions.map((opt) => {
              const MetricIcon = opt.icon || (opt.value === 'Vencida' ? AlertTriangle : FileText);
              const active = filterSubEstado === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setFilterSubEstado(active ? '' : opt.value)}
                  className={cn(
                    'text-left rounded-lg border-2 bg-card p-3 hover:shadow-md transition-shadow',
                    active ? 'border-primary' : 'border-border',
                  )}
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wide font-semibold opacity-80">
                    <MetricIcon className="h-3 w-3" />
                    {opt.label}
                  </div>
                  <div className="text-2xl font-bold mt-1 tabular-nums">{metrics[opt.value] || 0}</div>
                </button>
              );
            })}
          </div>
        )}

        {/* Filtros */}
        <Card>
          <CardContent className="p-3">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
              <div className="md:col-span-5 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Buscar por código, cliente, NIT o aeropuerto…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="md:col-span-3">
                <select
                  value={filterSubEstado}
                  onChange={(e) => setFilterSubEstado(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Todos los sub-estados</option>
                  {subEstadoOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2 relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="date"
                  value={filterFechaDesde}
                  onChange={(e) => setFilterFechaDesde(e.target.value)}
                  className="pl-9"
                  title="Fecha desde"
                />
              </div>
              <div className="md:col-span-1 relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="date"
                  value={filterFechaHasta}
                  onChange={(e) => setFilterFechaHasta(e.target.value)}
                  className="pl-9"
                  title="Fecha hasta"
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  type="button"
                  variant="outline"
                  onClick={limpiarFiltros}
                  disabled={!filtrosActivos}
                  className="w-full"
                  title="Limpiar filtros"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <AppliedFilters
              filters={{
                ...(filterSubEstado && { subEstado: filterSubEstado }),
                ...(filterFechaDesde && { fechaDesde: filterFechaDesde }),
                ...(filterFechaHasta && { fechaHasta: filterFechaHasta }),
              }}
              fields={[
                { id: 'subEstado', label: 'Sub-estado', options: subEstadoOptions },
                { id: 'fechaDesde', label: 'Desde' },
                { id: 'fechaHasta', label: 'Hasta' },
              ]}
              onRemoveFilter={(k) => {
                if (k === 'subEstado') setFilterSubEstado('');
                if (k === 'fechaDesde') setFilterFechaDesde('');
                if (k === 'fechaHasta') setFilterFechaHasta('');
              }}
              onClear={limpiarFiltros}
            />
          </CardContent>
        </Card>

        <DataTable
          data={pageItems}
          columns={tableColumns}
          isLoading={loading}
          onAction={handleTableAction}
          emptyMessage={`Sin solicitudes en ${titulo.toLowerCase()}`}
          emptyHint={
            rows.length === 0
              ? `No hay solicitudes aéreas en estado ${estado}.`
              : 'Ninguna solicitud coincide con los filtros aplicados.'
          }
        />

        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={filtered.length}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={setCurrentPage}
          />
        )}

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar la solicitud aérea?</AlertDialogTitle>
              <AlertDialogDescription>
                Se eliminará <strong>{deleteTarget ? formatCodigo(deleteTarget) : ''}</strong>. Esta acción no se
                puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete}>Eliminar</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AirRequestDetailDialog
          request={detailRequest}
          open={!!detailRequest}
          onOpenChange={(o) => !o && setDetailRequest(null)}
          onChanged={() => load({ silencioso: true })}
        />

        {permiteInvitar && (
          <ProviderInvitationModal
            open={!!invitationRequest}
            onOpenChange={(o) => !o && setInvitationRequest(null)}
            airRequest={invitationRequest}
            onSent={() => load()}
          />
        )}

        {cotizacionRequest && (
          <CargarAirCotizacionDialog
            open
            onOpenChange={(o) => !o && setCotizacionRequest(null)}
            airRequest={cotizacionRequest}
            onSaved={() => load({ silencioso: true })}
          />
        )}

        {comparadorRequest && (
          <ComparadorCotizacionesAereoDialog
            open
            onOpenChange={(o) => !o && setComparadorRequest(null)}
            airRequest={comparadorRequest}
            onChanged={() => load({ silencioso: true })}
          />
        )}

        {/* Trazabilidad */}
        <Dialog open={!!traceabilityRequest} onOpenChange={(o) => !o && setTraceabilityRequest(null)}>
          <DialogContent className="w-[80vw] max-w-none max-h-[90vh] p-0 overflow-hidden flex flex-col">
            <DialogHeader className="px-6 py-4 border-b bg-muted/30 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <div className="space-y-0.5">
                  <DialogTitle className="text-lg">
                    Trazabilidad · {traceabilityRequest?.codigo || shortId(traceabilityRequest?._id)}
                  </DialogTitle>
                  <DialogDescription className="text-xs">
                    Cliente: {formatCliente(traceabilityRequest || {})} · {formatRuta(traceabilityRequest || {})}
                  </DialogDescription>
                </div>
              </div>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
              {traceabilityRequest ? (
                <TraceabilitySection
                  airRequestId={traceabilityRequest._id}
                  clienteSnapshot={traceabilityRequest.clienteSnapshot}
                />
              ) : null}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </BoardPageTransition>
  );
}
